import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Autocomplete,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Container,
  Paper,
  Backdrop,
  CircularProgress,
  Snackbar,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useMutation } from 'react-query';
import client from '../../API';
import { getPONumber } from '../../Utils/constants';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const PayPage = () => {
  const [step, setStep] = useState(0);
  const [creators, setCreators] = useState([]);
  const [batchPayouts, setBatchPayouts] = useState([
    { paymentAmount: '', selectedCreator: null, poNumber: getPONumber('TCC') },
  ]);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [companyData, setCompanyData] = useState({
    balance: 'Loading...',
    credit_line: 'Loading...',
  });
  const [companyUsers, setCompanyUsers] = useState([]);
  const [errorOpen, setErrorOpen] = useState(false);
  const [feePayer, setFeePayer] = useState('creator');
  const [nominalInterestRate, setNominalInterestRate] = useState(10);

  /**
   * ----------------------
   *  Data Fetching
   * ----------------------
   */
  const { mutate: fetchCreatorsData } = useMutation(() => client.creators.list({ is_vendor: true }), {
    onSuccess: (data) => {
      const formattedData = data.map((creator) => ({
        ...creator,
        label: creator.creator,
      }));
      setCreators(formattedData);
    },
    onError: (error) => console.error('Error fetching creators:', error),
  });

  const { mutate: fetchCompanyData } = useMutation(client.companies.listFetch, {
    onSuccess: (data) => {
      if (data && data.balance !== undefined && data.credit_line !== undefined) {
        setCompanyData({
          balance: parseFloat(data.balance).toFixed(2),
          credit_line: data.credit_line ? parseFloat(data.credit_line).toFixed(2) : 'No Credit Line Established',
        });
      }
      if (data && data.nominal_interest_rate !== undefined) {
        setNominalInterestRate(parseFloat(data.nominal_interest_rate));
      }
    },
    onError: (error) => {
      console.error('Error fetching company data:', error);
      setCompanyData({
        balance: 'Error loading balance',
        credit_line: 'Error loading credit line',
      });
    },
  });

  const { mutate: fetchCompanyUsers } = useMutation(() => client.users.publicList(), {
    onSuccess: (data) => setCompanyUsers(data.map((user) => user.email)),
    onError: (error) => console.error('Error fetching company users:', error),
  });

  const { mutate: createBatchPayout } = useMutation(client.payouts.createBatch, {
    onSuccess: () => {
      window.location.href = '/blitzpay';
    },
    onError: (error) => {
      console.error('Error during batch payout creation:', error);
      if (error.response && error.response.status === 500) setErrorOpen(true);
    },
    onSettled: () => setIsProcessing(false),
  });

  useEffect(() => {
    fetchCreatorsData();
    fetchCompanyData();
    fetchCompanyUsers();
  }, []);

  /**
   * ----------------------
   *  Helpers
   * ----------------------
   */
  const calculateSubtotal = () => batchPayouts.reduce((acc, p) => acc + parseFloat(p.paymentAmount || 0), 0);

  const calculateBlitzPayFee = (amount) => parseFloat(amount) * (nominalInterestRate / 100);

  const calculateTotal = () =>
    batchPayouts.reduce((acc, p) => {
      const amount = parseFloat(p.paymentAmount || 0);
      return feePayer === 'me' ? acc + amount / (1 - nominalInterestRate / 100) : acc + amount;
    }, 0);

  const calculateCreatorAmount = (amount) => {
    const parsed = parseFloat(amount || 0);
    return feePayer === 'me' ? parsed : parsed * (1 - nominalInterestRate / 100);
  };

  /**
   * ----------------------
   *  Event Handlers
   * ----------------------
   */
  const handleNextStep = () => {
    if (step === 0) {
      // Validation for step 0
      const incomplete = batchPayouts.some((p) => !p.selectedCreator || !p.paymentAmount);
      if (incomplete) {
        alert('Please complete all required fields for each payout.');
        return;
      }
      setStep(1);
    } else if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (emailRecipients.length === 0) {
        alert('Please add at least one email recipient.');
        return;
      }
      handlePaymentSubmission(batchPayouts);
    }
  };

  const handlePaymentSubmission = (payouts) => {
    setIsProcessing(true);
    const payload = {
      payouts: payouts.map((p) => ({
        creator_id: p.selectedCreator?.creator,
        amount: p.paymentAmount,
        bypassSMSVerification: false,
        po_number: p.poNumber,
      })),
      fee_payer: feePayer,
      notes,
      cc_emails: emailRecipients,
    };
    createBatchPayout(payload);
  };

  const handleAddPayout = () => {
    setBatchPayouts([
      ...batchPayouts,
      { paymentAmount: '', selectedCreator: null, poNumber: getPONumber('TCC') },
    ]);
  };

  const handleRemovePayout = (index) => {
    const newBatch = [...batchPayouts];
    newBatch.splice(index, 1);
    setBatchPayouts(newBatch);
  };

  const handleBatchChange = (index, field, value) => {
    if (field === 'poNumber') return; // PO is immutable
    const updated = batchPayouts.map((p, idx) => (idx === index ? { ...p, [field]: value } : p));
    setBatchPayouts(updated);
  };

  const handleFeePayerChange = (event) => setFeePayer(event.target.value);

  const handleCloseError = () => setErrorOpen(false);

  /**
   * ----------------------
   *  Render
   * ----------------------
   */
  return (
    <Container maxWidth="lg">
      <Box sx={{ margin: 2 }}>
        <Stepper activeStep={step}>
          <Step>
            <StepLabel>Select Creators</StepLabel>
          </Step>
          <Step>
            <StepLabel>Who Pays Blitz Pay Fee?</StepLabel>
          </Step>
          <Step>
            <StepLabel>Confirmation</StepLabel>
          </Step>
        </Stepper>
      </Box>

      <Paper sx={{ padding: 3, marginTop: 2 }}>
        {step === 0 && (
          <>
            <Typography variant="h5" gutterBottom>
              Select Creators
            </Typography>
            {batchPayouts.map((payout, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Autocomplete
                  options={creators}
                  getOptionLabel={(option) => option.label || ''}
                  value={payout.selectedCreator}
                  onChange={(e, newValue) => handleBatchChange(index, 'selectedCreator', newValue)}
                  renderInput={(params) => <TextField {...params} label="Select a Creator" required />}
                />
                <TextField
                  label="Payment Amount"
                  type="number"
                  fullWidth
                  value={payout.paymentAmount}
                  onChange={(e) => handleBatchChange(index, 'paymentAmount', e.target.value)}
                  required
                  sx={{ mt: 1 }}
                />
                {index > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Button variant="outlined" color="error" onClick={() => handleRemovePayout(index)}>
                      Remove Creator
                    </Button>
                  </>
                )}
              </Box>
            ))}
            <Button onClick={handleAddPayout} color="primary">
              Add Another Creator
            </Button>
          </>
        )}

        {step === 1 && (
          <>
            <Typography variant="h5" gutterBottom>
              Who Pays Blitz Pay Fee?
            </Typography>
            <Typography variant="body1" gutterBottom>
              Using Blitz Pay incurs a fee. The current nominal interest rate from your company is{' '}
              <b>{nominalInterestRate}%</b>. Choose who will pay it.
            </Typography>
            <FormControl component="fieldset">
              <FormLabel component="legend">Select who pays the Blitz Pay fee:</FormLabel>
              <RadioGroup name="feePayer" value={feePayer} onChange={handleFeePayerChange}>
                <FormControlLabel value="me" control={<Radio />} label="Me (Client)" />
                <FormControlLabel value="creator" control={<Radio />} label="Creator(s)" />
              </RadioGroup>
            </FormControl>
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="h5" gutterBottom>
              Confirmation
            </Typography>
            {batchPayouts.map((payout, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography>Creator: {payout.selectedCreator?.label}</Typography>
                <Typography>Amount: ${payout.paymentAmount}</Typography>
                <Typography>PO #: {payout.poNumber}</Typography>
                {index > 0 && <Divider sx={{ my: 2 }} />}
              </Box>
            ))}

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Nominal Interest (Fee) Rate: {nominalInterestRate}%</Typography>
              <Typography variant="h6" sx={{ mt: 2 }}>
                Total Fees: ${calculateBlitzPayFee(calculateSubtotal()).toFixed(2)}
              </Typography>
              <Typography variant="h6">
                Creators Will Receive: $
                {batchPayouts.reduce((acc, p) => acc + calculateCreatorAmount(p.paymentAmount), 0).toFixed(2)}
              </Typography>
              <Typography variant="h6">You'll Pay: ${calculateTotal().toFixed(2)}</Typography>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Current Credit Line: ${companyData.credit_line}</Typography>
              <Typography variant="h6">
                Remaining Credit Line:{' '}
                {companyData.credit_line === 'No Credit Line Established'
                  ? 'N/A'
                  : (parseFloat(companyData.credit_line) - calculateTotal()).toFixed(2)}
              </Typography>
            </Box>

            <Autocomplete
              multiple
              freeSolo
              options={companyUsers}
              value={emailRecipients}
              onChange={(e, newValue) => setEmailRecipients(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Email Recipients" placeholder="Add email recipients" required />
              )}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </>
        )}

        <Box sx={{ textAlign: 'right', marginTop: 3 }}>
          <Button variant="contained" color="primary" onClick={handleNextStep} disabled={isProcessing}>
            {step === 2 ? 'Confirm & Pay' : 'Next'}
          </Button>
        </Box>
      </Paper>

      <Backdrop open={isProcessing} sx={{ color: '#fff', zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar open={errorOpen} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error">
          Payment failed, please try again later.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PayPage;
